import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import {
	getTwoFactorMetadata,
	sendEmail2faCode,
	sendPhoneCall2faCode,
	sendSms2faCode,
	submitTwoFactorAuthCode
} from '@api/login';
import {
	TwoFactorAuthCodeForm,
	TwoFactorAuthenticationSuccessResponse,
	TwoFactorCodeDeliveryMethod,
	TwoFactorMetadata
} from 'types/authentication';
import { app } from '@store/modules/app';
import { sendMobileVerificationCode } from '@api/user';
import store from '..';

@Module({
	dynamic: true,
	namespaced: true,
	name: 'login',
	store
})
class LoginModule extends VuexModule {
	hasCompletedLoginMobileVerifySelect = false;
	selectedTwoFactorCodeDeliveryMethod: TwoFactorCodeDeliveryMethod = 'NONE';

	twoFactorMetadata: TwoFactorMetadata | null = null;

	@Action
	public async storeTwoFactorMetadata(): Promise<void> {
		if (this.selectedTwoFactorCodeDeliveryMethod === 'NONE') {
			const twoFactorMetadata = await getTwoFactorMetadata();

			this.UPDATE_TWO_FACTOR_METADATA(twoFactorMetadata);
			this.UPDATE_2FA_CODE_DELIVERY_METHOD(twoFactorMetadata.twoFactorType === 'TOTP' ? 'TOTP' : 'NONE');
		}
	}

	@Action
	public resetTwoFactorSelections(): void {
		this.UPDATE_2FA_CODE_DELIVERY_METHOD('NONE');
	}

	@Action
	public async submitLoginMobileVerifySelect(phoneNumberString: string): Promise<string> {
		this.UPDATE_HAS_COMPLETED_LOGIN_MOBILE_VERIFY_SELECT(true);
		await sendMobileVerificationCode(phoneNumberString);
		return 'login-mobile-verify-confirm';
	}

	@Action({ rawError: true })
	public async submitTwoFactorCode(twoFactorAuthForm: TwoFactorAuthCodeForm): Promise<string> {
		const successResponse: TwoFactorAuthenticationSuccessResponse = await submitTwoFactorAuthCode(
			twoFactorAuthForm
		);
		app.SET_OAUTH_TOKEN(successResponse.accessToken);
		app.SET_IS_AUTHENTICATED(true);
		this.UPDATE_2FA_CODE_DELIVERY_METHOD('NONE');
		this.UPDATE_TWO_FACTOR_METADATA(null);
		window.fundriseApp.requiresAdditionalAuthentication = false;
		return successResponse.redirectUrl;
	}

	@Action
	public async send2faCode(method: TwoFactorCodeDeliveryMethod): Promise<string> {
		const nextRoute = 'login-two-factor-confirm';
		switch (method) {
			case 'EMAIL':
				this.UPDATE_2FA_CODE_DELIVERY_METHOD('EMAIL');
				await sendEmail2faCode();
				return nextRoute;
			case 'SMS':
				this.UPDATE_2FA_CODE_DELIVERY_METHOD('SMS');
				await sendSms2faCode();
				return nextRoute;
			case 'PHONE_CALL':
				this.UPDATE_2FA_CODE_DELIVERY_METHOD('PHONE_CALL');
				await sendPhoneCall2faCode();
				return nextRoute;
			default:
				return '/';
		}
	}

	@Mutation
	public UPDATE_HAS_COMPLETED_LOGIN_MOBILE_VERIFY_SELECT(hasVisited: boolean): void {
		this.hasCompletedLoginMobileVerifySelect = hasVisited;
	}

	@Mutation
	public UPDATE_2FA_CODE_DELIVERY_METHOD(selectedTwoFactorCodeDeliveryMethod: TwoFactorCodeDeliveryMethod): void {
		this.selectedTwoFactorCodeDeliveryMethod = selectedTwoFactorCodeDeliveryMethod;
	}

	@Mutation
	public UPDATE_TWO_FACTOR_METADATA(twoFactorMetadata: TwoFactorMetadata | null): void {
		this.twoFactorMetadata = twoFactorMetadata;
	}
}

export const login = getModule(LoginModule);
